"use client";

import type { NicheFilters, NicheSortKey } from "@/lib/types";

type Props = {
  filters: NicheFilters;
  onChange: (next: NicheFilters) => void;
};

const SORT_OPTIONS: { value: NicheSortKey; label: string }[] = [
  { value: "opportunityScore", label: "Opportunity Score" },
  { value: "gapScore", label: "Gap Score" },
  { value: "delta", label: "Score Delta" },
  { value: "medianMrr", label: "Median MRR" },
  { value: "totalMrr", label: "Total MRR" },
  { value: "startupCount", label: "Startup Count" },
  { value: "averageGrowth30d", label: "Avg Growth" },
  { value: "forSaleRatio", label: "For Sale Ratio" },
  { value: "top3MrrShare", label: "Top 3 Share" }
];

export function NicheFilterBar({ filters, onChange }: Props) {
  return (
    <div className="filters filters--leaderboard">
      <div className="filter-group">
        <span className="filter-label">Sort by</span>
        <select
          className="filter-select"
          value={filters.sortBy}
          onChange={(e) =>
            onChange({ ...filters, sortBy: e.target.value as NicheSortKey })
          }
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <span className="filter-label">Min Score</span>
        <input
          className="filter-input"
          type="number"
          min={0}
          max={100}
          step={1}
          placeholder="e.g. 55"
          value={filters.minScore ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              minScore: e.target.value ? Number(e.target.value) : null
            })
          }
        />
      </div>

      <div className="filter-group">
        <span className="filter-label">Min Growth %</span>
        <input
          className="filter-input"
          type="number"
          step={1}
          placeholder="e.g. 10"
          value={filters.minGrowth ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              minGrowth: e.target.value ? Number(e.target.value) : null
            })
          }
        />
      </div>

      <div className="filter-group">
        <span className="filter-label">Max Startups</span>
        <input
          className="filter-input"
          type="number"
          min={1}
          step={1}
          placeholder="e.g. 40"
          value={filters.maxStartups ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              maxStartups: e.target.value ? Number(e.target.value) : null
            })
          }
        />
      </div>

      <div className="filter-group">
        <span className="filter-label">Min For Sale %</span>
        <input
          className="filter-input"
          type="number"
          min={0}
          max={100}
          step={5}
          placeholder="e.g. 20"
          value={filters.minForSaleRatio ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              minForSaleRatio: e.target.value ? Number(e.target.value) : null
            })
          }
        />
      </div>

      <div className="filter-group">
        <span className="filter-label">Min Gap Score</span>
        <input
          className="filter-input"
          type="number"
          min={0}
          step={1}
          placeholder="e.g. 100"
          value={filters.minGapScore ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              minGapScore: e.target.value ? Number(e.target.value) : null
            })
          }
        />
      </div>

      <button
        type="button"
        className={`filter-toggle ${filters.improvingOnly ? "filter-toggle--active" : ""}`}
        onClick={() => onChange({ ...filters, improvingOnly: !filters.improvingOnly })}
      >
        Improving only
      </button>

      <button
        type="button"
        className={`filter-toggle ${filters.highAcquisitionOnly ? "filter-toggle--active" : ""}`}
        onClick={() => onChange({ ...filters, highAcquisitionOnly: !filters.highAcquisitionOnly })}
      >
        High acquisition potential
      </button>
    </div>
  );
}
